'use client'

import BidIcon from '@/icons/bid'

import Link from 'next/link'
import { CheckCircle2, HandCoins } from 'lucide-react'
import useInscriptionModal from '@/utils/hooks/useInscriptionModal'
import { acceptedItemId } from '@/utils/offer-inbox'
import type { Offer } from '@/utils/offers'
import OfferItemSummary from './offer-item-summary'
import OfferAmount from './offer-amount'
import OfferMessage from './offer-message'
import OfferLinkArrow from './offer-link-arrow'
import type { OfferNotification } from './use-offer-notifications'
import styles from './offer-notifications.module.css'

/** What a row can ask the bell to open; the bell owns the dialogs so the popover can close first. */
export type OfferNotificationAction =
	| { type: 'review'; offer: Offer; action: 'accept' | 'decline' }
	| { type: 'update'; offer: Offer }
	| { type: 'cancel'; offer: Offer }

const when = (iso: string) =>
	new Date(iso).toLocaleString(undefined, {
		month: 'short',
		day: 'numeric',
		hour: 'numeric',
		minute: '2-digit'
	})

const expiryRemaining = (iso: string) => {
    const remaining = new Date(iso).getTime() - Date.now()
    if (remaining <= 0) return 'Expired'
    const days = Math.ceil(remaining / 86_400_000)
    if (remaining >= 86_400_000) return `Expires in ${days}d`
    const hours = Math.ceil(remaining / 3_600_000)
    if (remaining >= 3_600_000) return `Expires in ${hours}h`
    return `Expires in ${Math.max(1, Math.ceil(remaining / 60_000))}m`
}

/**
 * One offers row in the notifications bell: an offer waiting on my item
 * (accept or decline it here), a live bid of mine (update or cancel it here),
 * or a bid of mine a seller accepted (opens the item I now own).
 */
export function OfferNotificationRow({
	item,
	unread,
	onDone,
	onAction
}: {
	item: OfferNotification
	unread: boolean
	onDone: () => void
	onAction: (action: OfferNotificationAction) => void
}) {
	const openItem = useInscriptionModal()
	const { offer } = item
    const notificationTime = new Date(item.at).toISOString()
    const timestamp = <time className={styles.headlineTimestamp} dateTime={notificationTime} title={when(notificationTime)}>{when(notificationTime)}</time>
	if (item.kind === 'bid_accepted') {
		const itemId = acceptedItemId(offer)
		return (
			<li className={`${styles.offer} ${unread ? styles.unread : ''}`} data-testid="bid-accepted-row">
				<button
					type="button"
					className={styles.rowButton}
					aria-label="You bought this item. Offer accepted."
					onClick={() => {
						onDone()
						if (itemId) openItem(itemId)
					}}
				>
					<p className={styles.accepted}>
						<CheckCircle2 size={14} aria-hidden="true" /> <span>You bought • Offer accepted</span>{timestamp}
					</p>
					{itemId ? (
						<OfferItemSummary id={itemId} onNavigate={onDone}>
							<OfferAmount sats={offer.price_sats} showLabel={false} showApproximation={false} compact />
						</OfferItemSummary>
					) : (
						<OfferAmount sats={offer.price_sats} showLabel={false} showApproximation={false} compact />
					)}
				</button>
			</li>
		)
	}
	if (item.kind === 'bid_active') {
		const target =
			offer.scope === 'item' && offer.inscription_id ? null : offer.collection_slug || 'collection'
		return (
			<li className={styles.offer} data-testid="bid-active-row">
				<p className={styles.accepted}>
					<BidIcon size={14} aria-hidden="true" /> <span>You made an offer</span>{timestamp}
				</p>
				{offer.scope === 'item' && offer.inscription_id ? (
					<OfferItemSummary id={offer.inscription_id} onNavigate={onDone}>
						<OfferAmount sats={offer.price_sats} showLabel={false} showApproximation={false} compact />
						<span className={styles.timestamp}>
							<time dateTime={offer.expires_at} title={new Date(offer.expires_at).toLocaleString()}>{expiryRemaining(offer.expires_at)}</time>
						</span>
					</OfferItemSummary>
				) : (
					<p className={styles.target}>
						<Link href={`/collection/${target}`} onClick={onDone}>
							{target}
						</Link>
						<OfferAmount sats={offer.price_sats} showLabel={false} showApproximation={false} compact />
						<span className={styles.timestamp}>
							<time dateTime={offer.expires_at} title={new Date(offer.expires_at).toLocaleString()}>{expiryRemaining(offer.expires_at)}</time>
						</span>
					</p>
				)}
				<div className={styles.actions}>
					<button type="button" className={styles.primaryAction} onClick={() => onAction({ type: 'update', offer })}>
						Update offer
					</button>
					<button type="button" className={styles.secondaryAction} onClick={() => onAction({ type: 'cancel', offer })}>
						Cancel &amp; refund
					</button>
				</div>
			</li>
		)
	}
	return (
		<li className={`${styles.offer} ${unread ? styles.unread : ''}`} data-testid="offer-waiting-row">
			<p className={styles.accepted}>
				<HandCoins size={14} aria-hidden="true" /> <span>Offer on your item</span>{timestamp}
			</p>
			<OfferItemSummary id={offer.inscription_id!} priceSats={offer.price_sats} onNavigate={onDone}>
				<OfferAmount sats={offer.price_sats} showLabel={false} showApproximation={false} compact />
				<span className={styles.timestamp}>
					<time dateTime={offer.expires_at} title={new Date(offer.expires_at).toLocaleString()}>{expiryRemaining(offer.expires_at)}</time>
				</span>
			</OfferItemSummary>
			<OfferMessage offer={offer} message={offer.message} compact />
			<div className={styles.actions}>
				<button type="button" className={styles.primaryAction} onClick={() => onAction({ type: 'review', offer, action: 'accept' })}>
					Accept offer
				</button>
				<button type="button" className={styles.secondaryAction} onClick={() => onAction({ type: 'review', offer, action: 'decline' })}>
					Decline
				</button>
			</div>
		</li>
	)
}

export function ManageOffersLink({ onDone }: { onDone: () => void }) {
	return (
		<Link href="/wallet#offers" className={styles.manage} onClick={onDone}>
			Manage all offers <OfferLinkArrow />
		</Link>
	)
}
